import { useEffect, useState } from "react";
import { Loader2, Plus, Trash2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { http, errMsg } from "../lib/api";

const inputCls = "w-full rounded-lg border border-slate-200 px-2.5 py-2 text-sm outline-none focus:border-[#EC5B24]";

const ItemRow = ({ it, i, photos, set, remove }) => (
  <div className="grid gap-2 grid-cols-[64px_1fr] sm:grid-cols-[64px_1.2fr_1.5fr_90px_90px_1.3fr_36px] items-center border-b border-dashed py-2" data-testid={`meal-item-${i}`}>
    <img src={it.image} alt="" className="h-14 w-16 rounded-lg object-cover bg-slate-100" />
    <input data-testid={`meal-name-${i}`} value={it.name} onChange={(e) => set({ name: e.target.value })} placeholder="Meal name" className={inputCls} />
    <input data-testid={`meal-desc-${i}`} value={it.desc} onChange={(e) => set({ desc: e.target.value })} placeholder="Short description" className={`${inputCls} col-span-2 sm:col-span-1`} />
    <input data-testid={`meal-price-${i}`} value={it.price} onChange={(e) => set({ price: e.target.value.replace(/\D/g, "") })} placeholder="₹ price" className={`${inputCls} font-mono`} />
    <select data-testid={`meal-veg-${i}`} value={it.veg ? "veg" : "nonveg"} onChange={(e) => set({ veg: e.target.value === "veg" })} className={`${inputCls} bg-white`}>
      <option value="veg">Veg</option><option value="nonveg">Non-veg</option>
    </select>
    <select data-testid={`meal-photo-${i}`} value={Object.keys(photos).find((k) => photos[k] === it.image) || ""} onChange={(e) => set({ image: photos[e.target.value] })} className={`${inputCls} bg-white col-span-2 sm:col-span-1`}>
      <option value="" disabled>Custom photo</option>{Object.keys(photos).map((k) => <option key={k} value={k}>{k.replace(/_/g, " ")}</option>)}
    </select>
    <button data-testid={`meal-remove-${i}`} onClick={remove} className="h-9 w-9 grid place-items-center rounded-lg text-red-500 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
  </div>
);

const GroupCard = ({ g, photos, onSaved }) => {
  const [items, setItems] = useState(g.items);
  const [busy, setBusy] = useState(false);
  const setItem = (i, patch) => setItems(items.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const add = () => setItems([...items, { name: "", desc: "", price: "", veg: true, image: Object.values(photos)[0] }]);
  const save = async () => {
    const free = g.group === "AI";
    if (items.some((x) => x.name.trim().length < 2 || x.price === "" || (!free && !(+x.price > 0)))) return toast.error(free ? "Every meal needs a name and a price (₹0 = free)" : "Every meal needs a name and a price above ₹0");
    setBusy(true);
    try { await http.put(`/admin/meals/${g.group}`, { brand: g.brand, items: items.map((x) => ({ ...x, price: +x.price })) }); toast.success(`${g.label} meals saved`); onSaved(); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const reset = () => http.delete(`/admin/meals/${g.group}`).then(() => { toast.success("Reset to default menu"); onSaved(); }).catch((e) => toast.error(errMsg(e)));
  return (
    <div className="bg-white rounded-2xl border p-5" data-testid={`meal-group-${g.group}`}>
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <h3 className="font-heading font-bold text-lg">{g.label}</h3>
        {g.custom && <span className="text-[10px] font-bold uppercase bg-orange-50 text-[#EC5B24] rounded-full px-2 py-0.5">Your prices</span>}
        <div className="ml-auto flex gap-2">
          {g.custom && <button data-testid={`meal-reset-${g.group}`} onClick={reset} className="rounded-full border px-3 py-1.5 text-xs font-semibold flex items-center gap-1"><RotateCcw className="h-3 w-3" /> Default</button>}
          <button data-testid={`meal-add-${g.group}`} onClick={add} className="rounded-full border px-3 py-1.5 text-xs font-semibold flex items-center gap-1"><Plus className="h-3 w-3" /> Add meal</button>
          <button data-testid={`meal-save-${g.group}`} disabled={busy} onClick={save} className="rounded-full bg-[#1C1A4A] text-white px-4 py-1.5 text-xs font-semibold flex items-center gap-1">{busy && <Loader2 className="h-3 w-3 animate-spin" />} Save</button>
        </div>
      </div>
      {items.map((it, i) => <ItemRow key={i} it={it} i={i} photos={photos} set={(p) => setItem(i, p)} remove={() => setItems(items.filter((_, j) => j !== i))} />)}
    </div>
  );
};

export const MealEditor = () => {
  const [d, setD] = useState(null);
  const load = () => http.get("/admin/meals").then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  if (!d) return <Loader2 className="h-6 w-6 animate-spin text-[#EC5B24]" />;
  return (
    <div className="space-y-4" data-testid="admin-meals">
      <p className="text-sm text-slate-500">Prices are per meal, per traveller, per flight. Changes apply to new bookings right away.</p>
      {d.groups.map((g) => <GroupCard key={g.group + JSON.stringify(g.items)} g={g} photos={d.photos} onSaved={load} />)}
    </div>
  );
};
